/**
 * Pure helpers for the Departments pages: paths, ids, errors, stamps, names.
 */

// ---------------------------------------------------------------- paths + ids

export const DEPARTMENTS_BASE_PATH = '/app/departments';
export const DEPARTMENTS_LIST_PATH = `${DEPARTMENTS_BASE_PATH}/list`;
export const DESIGNATIONS_PATH = `${DEPARTMENTS_BASE_PATH}/designations`;
export const DEPARTMENT_MEMBERS_PATH = `${DEPARTMENTS_BASE_PATH}/members`;
export const MINISTRIES_PATH = `${DEPARTMENTS_BASE_PATH}/ministries`;
export const MINISTRY_MEMBERS_PATH = `${MINISTRIES_PATH}/members`;
export const MINISTRY_TEAMS_PATH = `${MINISTRIES_PATH}/teams`;
export const MINISTRY_EQUIPMENT_PATH = `${MINISTRIES_PATH}/equipment`;
export const MINISTRY_ACTIVITIES_PATH = `${MINISTRIES_PATH}/activities`;
export const MINISTRY_ROLES_PATH = `${DEPARTMENTS_BASE_PATH}/settings/ministry-roles`;

export type DepartmentTab = 'overview' | 'members' | 'designations';
export type MinistryTab = 'members' | 'teams' | 'equipment' | 'activities';

export const departmentPath = (departmentId: number, tab?: DepartmentTab) =>
  `${DEPARTMENTS_BASE_PATH}/view/${departmentId}${tab ? `?tab=${tab}` : ''}`;
export const ministryPath = (ministryId: number, tab?: MinistryTab) =>
  `${MINISTRIES_PATH}/${ministryId}${tab ? `?tab=${tab}` : ''}`;

/** Admin member profile (Admin or Member.Read, same gate as the member endpoints). */
export const memberProfilePath = (memberId: number) => `/admin/members/${memberId}`;

/** Route param → id. Anything that is not a positive whole number is null. */
export function parseId(param: string | undefined | null): number | null {
  if (param == null || !/^\d+$/.test(param)) return null;
  const n = Number(param);
  return Number.isSafeInteger(n) && n > 0 ? n : null;
}

/** Select value ("12" or null) → id. */
export function selectToId(value: string | null | undefined): number | null {
  return parseId(value ?? null);
}

// ---------------------------------------------------------------- errors

interface SdkErrorShape {
  message?: string;
  status?: number;
  statusCode?: number;
  errors?: unknown;
}

export function errorStatus(err: unknown): number | undefined {
  const e = err as SdkErrorShape | null;
  return e?.status ?? e?.statusCode ?? undefined;
}

function errorList(err: unknown): string[] {
  const e = err as SdkErrorShape | null;
  return Array.isArray(e?.errors) ? (e!.errors as unknown[]).filter((x): x is string => typeof x === 'string') : [];
}

/**
 * One readable sentence for a failed call: the server's message when it has one (the
 * DepartmentManagement API puts the reason in `message`), a plain explanation for 401 / 403 /
 * 404 / timeouts.
 */
export function errorMessage(err: unknown, fallback = 'Something went wrong. Please try again.'): string {
  if (!err) return fallback;
  const status = errorStatus(err);
  const e = err as SdkErrorShape;
  const detail = [e.message, ...errorList(err)].filter((x): x is string => !!x && x.trim().length > 0);
  if (status === 401) return 'Your session has expired. Sign in again.';
  if (status === 403) return 'You do not have permission to do this.';
  if (status === 404) return detail[0] || 'It no longer exists. It may have been removed by someone else.';
  if (status === 408) return 'The server took too long to answer. Check your connection and try again.';
  return detail.length ? detail.join(' ') : fallback;
}

// ---------------------------------------------------------------- stamps

/** yyyyMMddHHmmss (or yyyyMMdd) number → Date. */
export function stampToDate(stamp: number | string | null | undefined): Date | null {
  if (stamp == null) return null;
  const s = String(stamp).replace(/\D/g, '');
  if (s.length < 8 || /^0+$/.test(s)) return null;
  const y = Number(s.slice(0, 4));
  const mo = Number(s.slice(4, 6)) - 1;
  const d = Number(s.slice(6, 8));
  const h = s.length >= 10 ? Number(s.slice(8, 10)) : 0;
  const mi = s.length >= 12 ? Number(s.slice(10, 12)) : 0;
  const date = new Date(y, mo, d, h, mi);
  return Number.isNaN(date.getTime()) || y < 1900 ? null : date;
}

export function formatStamp(stamp: number | string | null | undefined, fallback = 'Not recorded'): string {
  const d = stampToDate(stamp);
  return d ? d.toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' }) : fallback;
}

// ---------------------------------------------------------------- numbers + text

const numberFormat = new Intl.NumberFormat('en-ZA');
export const formatNumber = (n: number | null | undefined) => numberFormat.format(Number(n ?? 0));

export const plural = (n: number, one: string, many?: string) => `${formatNumber(n)} ${n === 1 ? one : many ?? `${one}s`}`;

export const byName = <T extends { name?: string | null }>(a: T, b: T) =>
  (a.name ?? '').localeCompare(b.name ?? '', undefined, { sensitivity: 'base' });

/** Search helper: every term must appear in one of the fields. */
export function matchesSearch(search: string, ...fields: Array<string | null | undefined>): boolean {
  const terms = search.toLowerCase().split(/\s+/).filter(Boolean);
  if (terms.length === 0) return true;
  const hay = fields.map((f) => (f ?? '').toLowerCase()).join(' ');
  return terms.every((t) => hay.includes(t));
}

// ---------------------------------------------------------------- people

/** Display name for a member row from the department views. */
export function personName(
  m: { memberFullName?: string | null; memberFirstName?: string | null; memberSurname?: string | null; firstName?: string | null; lastName?: string | null; memberEmail?: string | null; email?: string | null; memberID?: number } | null | undefined
): string {
  if (!m) return 'Unknown member';
  const full = (m.memberFullName ?? '').trim() || `${m.memberFirstName ?? m.firstName ?? ''} ${m.memberSurname ?? m.lastName ?? ''}`.trim();
  return full || (m.memberEmail ?? m.email ?? '').trim() || `Member #${m.memberID ?? '?'}`;
}

export function initials(name: string): string {
  const parts = name.replace(/[^A-Za-z0-9 ]/g, ' ').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  return parts.length === 1 ? parts[0].slice(0, 2).toUpperCase() : `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

/** "Member #123" rows are memberships whose person could not be resolved. */
export const isUnresolvedName = (name: string) => /^Member #\d+$/.test(name);
